import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { Lock } from "lucide-react";
import { GoldFoilSeal } from "./documents/_shared";
import { numberToWords as numWords } from "@/lib/format";

// Self-contained, printable Private Negotiable Instrument (Private Negotiable Instrument) instrument —
// business-check format (8.5" x 3.5"). Used inside the Closing Binder's Part V
// (Funding) so the draft referenced by the Standstill package actually prints.

const checkWords = (amount) => {
  const n = Number(amount || 0);
  const dollars = Math.floor(n);
  const cents = Math.round((n - dollars) * 100);
  const words = numWords(dollars).replace(/ Dollars.*$/, "").trim() || "Zero";
  return `${words} and ${String(cents).padStart(2, "0")}/100`;
};
const fmtMoney = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MicrTransit = () => (
  <svg width="15" height="22" viewBox="0 0 15 22" style={{ verticalAlign: "middle" }} aria-hidden>
    <rect x="1.5" y="2" width="3.5" height="18" fill="#0A0A0A" />
    <rect x="10" y="2" width="3.5" height="18" fill="#0A0A0A" />
  </svg>
);
const MicrOnUs = () => (
  <svg width="15" height="22" viewBox="0 0 15 22" style={{ verticalAlign: "middle" }} aria-hidden>
    <rect x="5.5" y="1" width="4" height="14" fill="#0A0A0A" />
    <rect x="1.5" y="15" width="12" height="5.5" fill="#0A0A0A" />
  </svg>
);

export default function UsanceDraftInstrument({
  draftNo = "UTD-________", amount = 0, drawerName = "SEUTRADE FINANCIAL SERVICES",
  drawerAddress = "213 N Market Street #1011, Wilmington, DE 19801", payTo = "", payAcct = "____________",
  payRouting = "____________", usanceDays = 90, usanceTerm = "acceptance",
  dateOfIssue = "", maturityDate = "", signerName = "", signerTitle = "Authorized Officer",
  acceptingBankName = "", acceptanceOfficer = "", acceptanceTitle = "", acceptanceDate = "",
  commercialAgreementNo = "", purpose = "", auth = {}, fmtLongDate = (x) => x,
}) {
  const barcodeRef = useRef(null);
  useEffect(() => {
    if (barcodeRef.current) {
      try { JsBarcode(barcodeRef.current, draftNo || "UTD", { format: "CODE128", width: 1.3, height: 30, fontSize: 10, margin: 0 }); } catch { /* noop */ }
    }
  }, [draftNo]);

  const words = checkWords(amount);
  const micrCheck = (String(draftNo || "").replace(/\D/g, "") || "0001").slice(-6);
  const micrRouting = String(payRouting || "").replace(/\D/g, "") || "021000021";
  const micrAccount = String(payAcct || "").replace(/\D/g, "") || "0000000000";
  const caNo = commercialAgreementNo || ("CA-" + String(draftNo).replace(/[^0-9A-Za-z]/g, "").slice(-8).toUpperCase());
  const hash = String(auth.hash || "");
  const med = String(auth.medallion || "");

  return (
    <>
      {/* Instrument summary + contents */}
      <div className="paper-sheet legal-page" data-testid="ss-utd-summary">
        <div className="lg-eyebrow">Part V — Funding · Private Negotiable Instrument (Private Negotiable Instrument)</div>
        <h1 className="lg-title">Private Negotiable Instrument — Funding Instrument</h1>
        <div className="lg-rule" />
        <table className="lg-table"><tbody>
          <tr><td>Draft No.</td><td>{draftNo}</td></tr>
          <tr><td>Purpose</td><td>{purpose || "Payoff & satisfaction of the Existing Loan (Standstill / Refinancing)"}</td></tr>
          <tr><td>Drawer / Maker</td><td>{drawerName}</td></tr>
          <tr><td>Payee / Beneficiary (Payoff Recipient)</td><td>{payTo || "____________________"}</td></tr>
          <tr><td>Face Amount</td><td>USD {fmtMoney(amount)} ({words} U.S. Dollars)</td></tr>
          <tr><td>Usance / Tenor</td><td>{usanceDays} days after {usanceTerm === "sight" ? "sight" : "acceptance"}</td></tr>
          <tr><td>Date of Acceptance / Issue</td><td>{fmtLongDate(dateOfIssue)}</td></tr>
          <tr><td>Maturity Date</td><td>{fmtLongDate(maturityDate)}</td></tr>
          <tr><td>Authorized Signer (Drawer)</td><td>{signerName}, {signerTitle}</td></tr>
          <tr><td>Commercial Agreement No.</td><td>{caNo}</td></tr>
        </tbody></table>
        <p className="lg-note">This Private Negotiable Instrument is the funding instrument that remits the payoff to the Existing Lender through Escrow under the 180-Day Standstill &amp; Refinancing package. It is structured for presentment to, and acceptance by, a bank, accepting institution, or fiscal agent. <strong>Draft / template for review by counsel — not for submission as funded capital unless actually accepted and funded by a bank.</strong></p>
      </div>

      {/* The instrument (business-check size 8.5 x 3.5) */}
      <div className="paper-sheet check-page" data-testid="ss-utd-draft">
        <div className="utd-frame">
          <div className="utd-watermark">{drawerName}</div>
          <div className="utd-strip">
            <span className="utd-strip-main">USANCE TIME DRAFT · PRIVATE NEGOTIABLE INSTRUMENT — DISCOUNT-GRADE</span>
            <span className="utd-strip-sub">NEGOTIABLE INSTRUMENT (UCC §3-104) · TRUE WATERMARK &amp; SECURITY FEATURES</span>
          </div>
          <div className="utd-body">
            <div className="utd-r1">
              <div className="utd-drawer">
                <strong>{drawerName}</strong>
                {String(drawerAddress || "").split("\n").map((l, i) => <span key={i} className="utd-sm">{l}</span>)}
              </div>
              <div className="utd-barcenter">
                <svg ref={barcodeRef} className="utd-barcode" data-testid="ss-utd-barcode" />
                <span className="utd-barlbl">DRAFT NO · CODE-128 · VERIFY</span>
              </div>
              <div className="utd-meta">
                <div>Draft No. <span className="utd-red">{draftNo}</span></div>
                <div>Date of Issue: <strong>{fmtLongDate(dateOfIssue)}</strong></div>
              </div>
            </div>

            <div className="utd-r2">
              <div className="utd-payto">PAY TO THE<br />ORDER OF</div>
              <div className="utd-payline">{payTo}</div>
              <div className="utd-amtbox"><span className="utd-dollar">$</span><span className="utd-amt">{fmtMoney(amount)}</span></div>
            </div>

            <div className="utd-r3">
              <span className="utd-words">{words}</span>
              <span className="utd-usd">U.S. DOLLARS</span>
            </div>

            <div className="utd-r4">
              <div className="utd-terms">
                <div><span className="utd-box">{usanceTerm === "acceptance" ? "✓" : ""}</span> {usanceDays} days after acceptance &nbsp;
                  <span className="utd-box">{usanceTerm === "sight" ? "✓" : ""}</span> after sight</div>
                <div>Maturity: <strong>{fmtLongDate(maturityDate)}</strong></div>
                <div>Payable at: {payTo} · Acct {payAcct} · {payRouting}</div>
              </div>
              <div className="utd-sig">
                <div className="utd-signature">{signerName}</div>
                <div className="utd-sigrule" />
                <div className="utd-sigcap"><span>Authorized Signature — Drawer</span><span>{drawerName}</span></div>
              </div>
            </div>

            <div className="utd-bottom">
              <div className="utd-bl">
                <div className="utd-r5">
                  <strong>UNCONDITIONAL ORDER TO PAY (UCC §3-104):</strong> on the maturity date, pay the fixed sum shown — <strong>FOR VALUE RECEIVED</strong> — under Commercial Agreement No. {caNo} · Purpose: {purpose || "Existing-Loan payoff"}. Negotiable; authorized for discount, pledge, hypothecation, assignment &amp; negotiation. Holder's transfer/endorsement is <strong>WITHOUT RECOURSE</strong> (UCC §3-415(b)).
                </div>
                <div className="utd-esign">
                  <span className="utd-esign-h">⬢ E-SIGN / UETA AUTHENTICATED</span>
                  <span className="utd-esign-x" title={`Authentication SHA-256: ${hash}`}>15 U.S.C. §7001 · SHA-256 {hash.slice(0, 24)}…</span>
                  <span className="utd-esign-x">{auth.ts ? new Date(auth.ts).toUTCString() : ""}</span>
                  <span className="utd-medallion" title={`S.T.A.M.P. Medallion Signature Guarantee · M-${med.slice(0, 6).toUpperCase()}`}>
                    <span className="utd-med-badge">✹ S.T.A.M.P. MEDALLION GUAR. M-{med.slice(0, 6).toUpperCase() || "——————"}</span>
                    <span className="utd-esign-x">Medallion SHA-256 {med.slice(0, 20)}…</span>
                  </span>
                </div>
              </div>
              <div className="utd-acceptbox" data-testid="ss-utd-acceptance">
                <div style={{ position: "absolute", right: 6, top: 4 }}>
                  <GoldFoilSeal size={52} label="" />
                </div>
                <div className="utd-acc-h">ACCEPTANCE — PAYEE / ACCEPTING BANK OR FISCAL AGENT</div>
                {acceptingBankName && acceptanceOfficer ? (
                  <>
                    <div className="utd-acc-stamp">✓ ACCEPTED — ENGAGED TO PAY AT MATURITY</div>
                    <div className="utd-acc-note">Accepted and engaged to pay at maturity per the terms hereof (UCC §3-409/§3-410) by <strong>{acceptingBankName}</strong>.</div>
                    <div className="utd-acc-officersig">{acceptanceOfficer}</div>
                    <div className="utd-acc-sigline" />
                    <div className="utd-acc-cap"><span>Authorized Signature</span><span>{acceptingBankName}</span></div>
                    <div className="utd-acc-fields"><span>By: {acceptanceOfficer}</span><span>Title: {acceptanceTitle || "—"}</span><span>Date: {acceptanceDate ? fmtLongDate(acceptanceDate) : "—"}</span></div>
                  </>
                ) : (
                  <>
                    <div className="utd-acc-note">Accepted and engaged to pay at maturity per the terms hereof (UCC §3-409/§3-410).</div>
                    <div className="utd-acc-sigline" />
                    <div className="utd-acc-cap"><span>Authorized Signature</span><span>{acceptingBankName || payTo}</span></div>
                    <div className="utd-acc-fields"><span>By: __________________</span><span>Title: ____________</span><span>Date: __________</span></div>
                  </>
                )}
              </div>
            </div>

            <div className="utd-micr" data-testid="ss-utd-micr">
              <span className="micr-grp"><MicrOnUs /><span className="micr">{micrCheck}</span><MicrOnUs /></span>
              <span className="micr-grp"><MicrTransit /><span className="micr">{micrRouting}</span><MicrTransit /></span>
              <span className="micr-grp"><span className="micr">{micrAccount}</span><MicrOnUs /></span>
              <Lock size={18} className="utd-lock" />
            </div>
          </div>
        </div>
        <div className="check-note no-print">Sized 8.5″ × 3.5″ — load standard business check stock (check at top of page) and print.</div>
      </div>

      <style>{`
        .check-page { width:8.5in; height:3.5in; overflow:hidden; position:relative; }
        @media print { .check-page { padding:0 !important; } }
        .utd-frame { position:relative; height:100%; border:3px solid #0e2a6b; background:linear-gradient(135deg,#eef1f8,#e6ebf5); display:flex; flex-direction:column; }
        .utd-frame::before { content:""; position:absolute; inset:5px; border:.5px solid #c3cce4; background-image:repeating-linear-gradient(45deg, rgba(14,42,107,.04) 0, rgba(14,42,107,.04) 1px, transparent 1px, transparent 7px); pointer-events:none; }
        .utd-watermark { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-7deg); font-size:40px; font-weight:800; color:rgba(14,42,107,.06); white-space:nowrap; pointer-events:none; }
        .utd-strip { background:#0e2a6b; color:#fff; text-align:center; padding:6px 4px 5px; position:relative; z-index:2; border-bottom:3px solid #b8860b; display:flex; flex-direction:column; gap:1px; }
        .utd-strip-main { font-size:11px; letter-spacing:.16em; font-weight:800; }
        .utd-strip-sub { font-size:7px; letter-spacing:.12em; font-weight:600; color:#c9d3ee; }
        .utd-body { position:relative; z-index:2; font-family:Georgia,'Times New Roman',serif; color:#1a1f2e; padding:4px 16px 2px; flex:1; display:flex; flex-direction:column; justify-content:space-between; }
        .utd-sm { display:block; font-size:8px; line-height:1.25; }
        .utd-r1 { display:flex; justify-content:space-between; align-items:flex-start; }
        .utd-drawer strong { font-size:11px; }
        .utd-meta { text-align:right; font-size:9.5px; line-height:1.5; }
        .utd-red { color:#b8101a; font-weight:700; }
        .utd-r2 { display:flex; align-items:flex-end; gap:8px; }
        .utd-payto { font-size:8px; font-weight:700; letter-spacing:.04em; line-height:1.1; }
        .utd-payline { flex:1; font-size:14px; border-bottom:1.3px solid #1a1f2e; padding:0 6px 2px; }
        .utd-amtbox { border:1.5px solid #1a1f2e; padding:3px 8px; display:flex; align-items:center; gap:6px; }
        .utd-dollar { font-size:13px; font-weight:700; }
        .utd-amt { font-size:15px; font-weight:800; }
        .utd-r3 { display:flex; align-items:flex-end; gap:8px; }
        .utd-words { flex:1; font-size:11px; border-bottom:1.3px solid #1a1f2e; padding:0 6px 2px; }
        .utd-usd { font-size:9px; letter-spacing:.08em; font-weight:600; }
        .utd-r4 { display:flex; justify-content:space-between; align-items:flex-end; gap:14px; }
        .utd-terms { font-size:8.5px; line-height:1.5; }
        .utd-box { display:inline-flex; width:9px; height:9px; border:1px solid #1a1f2e; align-items:center; justify-content:center; font-size:8px; font-weight:800; vertical-align:middle; }
        .utd-sig { width:230px; }
        .utd-signature { font-family:'Brush Script MT','Segoe Script',cursive; font-size:20px; color:#0e2a6b; text-align:center; line-height:1; }
        .utd-sigrule { border-bottom:1.2px solid #1a1f2e; margin-top:1px; }
        .utd-sigcap { display:flex; justify-content:space-between; font-size:7px; color:#3a4256; margin-top:2px; }
        .utd-bottom { display:flex; gap:14px; align-items:stretch; }
        .utd-bl { flex:1; display:flex; flex-direction:column; justify-content:space-between; }
        .utd-r5 { font-size:6.8px; line-height:1.28; color:#2a3147; }
        .utd-esign { margin-top:3px; border:1px solid #0b6b4f; border-left:3px solid #0b6b4f; background:rgba(11,107,79,.06); padding:2px 6px; display:flex; flex-direction:column; gap:0; }
        .utd-esign-h { font-size:7.5px; font-weight:800; letter-spacing:.06em; color:#0b6b4f; }
        .utd-esign-x { font-family:'Courier New',monospace; font-size:6.5px; color:#2a3147; }
        .utd-medallion { display:flex; flex-direction:column; gap:0; margin-top:2px; padding-top:2px; border-top:1px dashed #0b6b4f; }
        .utd-med-badge { display:inline-block; align-self:flex-start; font-size:6px; font-weight:900; letter-spacing:.04em; color:#0b6b4f; border:1px solid #0b6b4f; border-radius:999px; padding:0 5px; background:rgba(11,107,79,.1); }
        .utd-acceptbox { width:280px; border:1px solid #0e2a6b; padding:4px 7px; display:flex; flex-direction:column; position:relative; }
        .utd-acc-h { font-size:7.5px; font-weight:800; letter-spacing:.05em; color:#0e2a6b; text-align:center; border-bottom:.5px solid #98a3c2; padding-bottom:2px; }
        .utd-acc-note { font-size:7px; line-height:1.35; margin-top:3px; color:#2a3147; }
        .utd-acc-sigline { border-bottom:1.2px solid #1a1f2e; margin-top:7px; }
        .utd-acc-cap { display:flex; justify-content:space-between; font-size:7px; color:#3a4256; margin-top:2px; }
        .utd-acc-fields { display:flex; justify-content:space-between; font-size:7.5px; margin-top:4px; }
        .utd-acc-stamp { font-size:8px; font-weight:900; letter-spacing:.04em; color:#0b6b4f; border:1px solid #0b6b4f; border-radius:3px; padding:1px 4px; text-align:center; margin-top:3px; background:rgba(11,107,79,.08); }
        .utd-acc-officersig { font-family:'Brush Script MT','Segoe Script',cursive; font-size:12px; color:#0e2a6b; text-align:center; line-height:1; margin-top:2px; }
        .utd-micr { display:flex; align-items:center; gap:20px; background:rgba(255,255,255,.72); padding:1px 10px; border-radius:3px; margin-top:1px; height:26px; box-sizing:border-box; overflow:hidden; }
        .micr-grp { display:inline-flex; align-items:center; gap:6px; }
        .micr { font-family:'Courier New',monospace; font-weight:700; font-size:16px; letter-spacing:.18em; color:#0A0A0A; }
        .utd-lock { color:#0e2a6b; margin-left:auto; }
        .utd-barcenter { display:flex; flex-direction:column; align-items:center; justify-content:flex-start; flex:0 0 auto; padding-top:2px; }
        .utd-barcenter .utd-barcode { height:32px; }
        .utd-barcenter .utd-barlbl { font-size:6.5px; letter-spacing:.14em; color:#334; margin-top:2px; }
        .check-note { font-size:11px; color:#6c6c64; margin-top:6px; text-align:center; }
      `}</style>
    </>
  );
}
